import { ADMIN_HEADER, DEVICE_HEADER } from '../../shared/constants';
import type {
  ApiError,
  Code,
  CodeInput,
  CodesResponse,
  VoteKind,
  VoteResponse,
} from '../../shared/types';
import { getDeviceId } from './device';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, init);

  if (!response.ok) {
    const message = await response
      .json()
      .then((body: ApiError) => body.error)
      .catch(() => null);
    throw new Error(message ?? `Request failed (${response.status})`);
  }

  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

export function fetchCodes(): Promise<CodesResponse> {
  return request<CodesResponse>('/api/codes');
}

export function castVote(codeId: string, vote: VoteKind): Promise<VoteResponse> {
  return request<VoteResponse>(`/api/codes/${encodeURIComponent(codeId)}/vote`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', [DEVICE_HEADER]: getDeviceId() },
    body: JSON.stringify({ vote }),
  });
}

// --- Admin -----------------------------------------------------------------

function adminHeaders(key: string): HeadersInit {
  return { 'Content-Type': 'application/json', [ADMIN_HEADER]: key };
}

export function verifyAdminKey(key: string): Promise<{ ok: true }> {
  return request<{ ok: true }>('/api/admin/session', {
    method: 'POST',
    headers: adminHeaders(key),
  });
}

export function fetchAdminCodes(key: string): Promise<{ codes: Code[] }> {
  return request<{ codes: Code[] }>('/api/admin/codes', { headers: adminHeaders(key) });
}

export function createCode(key: string, input: CodeInput): Promise<{ code: Code }> {
  return request<{ code: Code }>('/api/admin/codes', {
    method: 'POST',
    headers: adminHeaders(key),
    body: JSON.stringify(input),
  });
}

export function updateCode(key: string, id: string, input: CodeInput): Promise<{ code: Code }> {
  return request<{ code: Code }>(`/api/admin/codes/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: adminHeaders(key),
    body: JSON.stringify(input),
  });
}

export function deleteCode(key: string, id: string): Promise<void> {
  return request<void>(`/api/admin/codes/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: adminHeaders(key),
  });
}
